import { useState, ReactNode } from 'react'
// Components
import CollectionCover from 'components/collection/cover'
import { Entity, Link } from '@tracklib/kit'
import PlayButton from 'components/playbutton'
import VideoModal from 'components/modal/video'

// Types
import { DiscoverEntityType } from 'types'

// Hooks
import { useTracking } from 'modules/tracking/hooks'

// Utils
import { getYoutubeIdFromUrl } from '@constants'

import styles from './entityblock.css'

type Artists = Array<{
  slug: string
  name: string
}>
type EntityBlockProps = {
  item: {
    id: string
    entity: [
      {
        id: string
        type: DiscoverEntityType
        value: string
        crate?: string
        title?: string
        image?: Array<string> | string
        url?: string
        slug?: string
        description?: string
        artists?: Artists
      },
    ]
    value?: string
    slug?: string
    artists?: Artists
    hide_image: boolean
    image?: string
    type: DiscoverEntityType
    title: string
    description: string
    mobileImage: string
    url: string
    background_color: string
    background_image?: number
    mobile_background_image?: number
    button_text: string
  }
  size: number
  hideLabel?: boolean
  hideTitle?: boolean
  hideDescription?: boolean
  hidePlay?: boolean
  identifier?: string
}
const EntityBlock = ({
  item: { entity: itemEntity },
  item,
  size,
  hideLabel,
  hideTitle,
  hideDescription,
  hidePlay,
  identifier = 'no identifier',
}: EntityBlockProps): JSX.Element => {
  const entity = itemEntity ? itemEntity[0] : null
  const trackEvent = useTracking()
  const [videoModalOpen, setVideoModalOpen] = useState(false)

  const { type, value } = entity || item
  const slug = item?.slug || entity?.slug

  const isVideo = type === 'video'
  const isFreeDownload = item?.type?.toLowerCase() === 'free download'

  const renderImage = (): ReactNode => {
    if (isVideo) {
      const id = getYoutubeIdFromUrl(value as string)
      return `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
    } else if ((!item && !entity) || item.hide_image) {
      return null
    } else if (item.image) {
      return String(item.image)
    } else if (type === 'collection' && entity && entity.id) {
      const collection = {
        crate: entity?.crate,
        covers: entity?.image as Array<string>,
      }
      return <CollectionCover collection={collection} />
    } else if (entity?.image) {
      return entity.image as string
    } else {
      return null
    }
  }

  const getPlayValue = (): string => {
    if (type === 'track') {
      return String(entity?.id || item.id)
    }
    if (type === 'collection' && entity && entity.id) {
      return String(entity.id)
    }
    if (type === 'artist' || type === 'label' || !value) {
      return slug as string
    }
    return value
  }

  const handleClick = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isVideo) {
      setVideoModalOpen(true)
    }
    if (e.target.tagName === 'path' || e.target.tagName === 'svg' || e.target.tagName === 'BUTTON') {
      // clicked on play button - handle below
      return null
    }
    return trackEvent('click entity', {
      eventCategory: 'entities',
      eventAction: 'click',
      eventLabel: identifier,
    })
  }

  const getType = (): string | undefined => {
    if (!hideLabel) {
      if (type === 'custom') {
        return undefined
      }
      if (isFreeDownload) {
        return entity?.type || 'Track'
      }
      if (item?.type) {
        return item.type
      }
      if (entity?.type) {
        return entity.type
      }
      return type
    }
    return undefined
  }

  const getUrl = () => {
    if (isVideo) {
      return ''
    }
    if (type === 'collection' && entity && entity.id) {
      return `/collection/${entity.id}/`
    }
    if (type === 'track') {
      return `/song/${slug}/`
    }
    return item.url || entity?.url || `/${type}/${slug}`
  }

  const getDescription = (): string | null | JSX.Element => {
    if (hideDescription) {
      return null
    }

    const artists = entity?.artists || item?.artists
    if (type === 'track' && artists?.length) {
      return (
        <div className={styles.artists}>
          {artists.map(artist => (
            <span key={artist.slug} title={artist.name}>
              <Link to={`/artist/${artist.slug}/`}>{artist.name}</Link>
              <span>, </span>
            </span>
          ))}
        </div>
      )
    }
    return (item.description || entity?.description) as string
  }
  const getBadge = (): 'free' | 'gem' | undefined => {
    if (isFreeDownload) {
      return 'free'
    }
    if (type === 'gem') {
      return 'gem'
    }
    return undefined
  }

  return (
    <>
      <Entity
        size={size}
        title={!hideTitle ? ((item.title || entity?.title) as string) : undefined}
        description={getDescription()}
        type={getType()}
        url={getUrl()}
        onClick={handleClick}
        image={renderImage()}
        mobileImage={item.mobileImage}
        backgroundColor={entity?.crate ? 'transparent' : item.background_color} // make bg transparent if crate
        backgroundImage={item.background_image}
        mobileBackgroundImage={item.mobile_background_image}
        buttonText={item.button_text}
        badge={getBadge()}
        overflowVisible={!!entity?.crate} // handle overflow in the cover component if crate
        hoverComponent={
          !hidePlay &&
          (entity || slug || type === 'video') &&
          type &&
          size !== 1 && (
            <PlayButton
              entity={type}
              value={getPlayValue()}
              contextKey={type === 'track' ? (entity?.slug as string) : undefined}
              onPlay={() =>
                trackEvent('click entity', {
                  eventCategory: 'entities',
                  eventAction: 'play',
                  eventLabel: identifier,
                })
              }
              hoverOnly
              datatesting="entity_play_button"
            />
          )
        }
      />
      {isVideo && <VideoModal videoUrl={value as string} isOpen={videoModalOpen} closeModal={() => setVideoModalOpen(false)} />}
    </>
  )
}

export default EntityBlock
